/* eslint-disable no-nested-ternary */
// Updated Sidebar.js with retractable functionality and pin option
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Drawer, CssBaseline, Hidden } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";

import SidebarContent from "./SidebarContent";
import TopAppBar from "../TopAppBar/TopAppBar";
import { getPageTitle } from "../../utils/utils";
import { logoutUser } from "../../redux/features/auth/actions";
import SidebarBg from "../../assets/images/sidebar-bg.svg";

const drawerWidth = 240;
const miniDrawerWidth = 60;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: (props) => (props.isCollapsed ? miniDrawerWidth : drawerWidth),
      flexShrink: 0,
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  },
  drawerPaper: {
    width: (props) => (props.isCollapsed ? miniDrawerWidth : drawerWidth),
    backgroundColor: "#0F3983",
    backgroundImage: `url(${SidebarBg})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    // hide scrollbars but still allow scrolling
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0),
      marginLeft: 0,
    },
  },
  toolbar: theme.mixins.toolbar,
  collapseButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    color: theme.palette.primary.contrastText,
  },
  collapseIcon: {
    color: theme.palette.primary.contrastText,
  },
}));

const Sidebar = ({ children, window }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isPinned, setIsPinned] = useState(true);
  const classes = useStyles({ isCollapsed: isCollapsed && !isPinned });
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const isRouteActive = (route) => location.pathname === route;

  const handleDrawerToggle = () => {
    setIsCollapsed(false);
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const handleNavigation = (path) => {
    history.push(path);
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (!isPinned) {
      setIsCollapsed(false);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setIsCollapsed(true);
    }
  };

  const handlePinToggle = () => {
    setIsPinned(!isPinned);
    if (!isPinned) {
      // When pinning, ensure sidebar is open
      setIsCollapsed(false);
    } else {
      // When unpinning, collapse the sidebar
      setIsCollapsed(true);
    }
  };

  const currentTitle = getPageTitle(location.pathname);
  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <div className={classes.root}>
      <CssBaseline />

      <TopAppBar
        currentTitle={currentTitle}
        handleDrawerToggle={handleDrawerToggle}
        isCollapsed={isCollapsed && !isPinned}
      />

      <nav className={classes.drawer} aria-label="navigation menu">
        <Hidden smUp implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <SidebarContent
              isRouteActive={isRouteActive}
              handleNavigation={handleNavigation}
              handleLogout={handleLogout}
              isCollapsed={false}
              isPinned={false}
              onPinToggle={() => {}} // No pin functionality on mobile
            />
          </Drawer>
        </Hidden>
        <Hidden xsDown implementation="css">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="permanent"
            open
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <SidebarContent
              isRouteActive={isRouteActive}
              handleNavigation={handleNavigation}
              handleLogout={handleLogout}
              isCollapsed={isCollapsed && !isPinned}
              isPinned={isPinned}
              onPinToggle={handlePinToggle}
            />
          </Drawer>
        </Hidden>
      </nav>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {children}
      </main>
    </div>
  );
};

Sidebar.propTypes = {
  children: PropTypes.node.isRequired,
  window: PropTypes.func,
};

export default Sidebar;
